'use client';

import React, { useState, useRef, KeyboardEvent } from 'react';
import { X } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';

interface TagInputProps {
	value: string[];
	onChangeAction: (tags: string[]) => void;
	placeholder?: string;
	className?: string;
	disabled?: boolean;
}

export function TagInput({
	value = [],
	onChangeAction,
	placeholder = 'Type and press Enter to add...',
	className = '',
	disabled = false
}: TagInputProps) {
	const [inputValue, setInputValue] = useState('');
	const inputRef = useRef<HTMLInputElement>(null);

	const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
		if (e.key === 'Enter' && inputValue.trim()) {
			e.preventDefault();
			addTag(inputValue.trim());
		} else if (e.key === 'Backspace' && !inputValue && value.length > 0) {
			// Delete last tag when backspace is pressed on empty input
			e.preventDefault();
			removeTag(value.length - 1);
		}
	};

	const addTag = (tag: string) => {
		if (tag && !value.includes(tag)) {
			onChangeAction([...value, tag]);
		}
		setInputValue('');
	};

	const removeTag = (index: number) => {
		const newTags = value.filter((_, i) => i !== index);
		onChangeAction(newTags);
	};

	const handleContainerClick = () => {
		if (inputRef.current && !disabled) {
			inputRef.current.focus();
		}
	};

	return (
		<div
			className={`
				min-h-[40px] p-2 border border-input rounded-md bg-background
				focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2
				cursor-text ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
				${className}
			`}
			onClick={handleContainerClick}
		>
			<div className="flex flex-wrap gap-1">
				{value.map((tag, index) => (
					<Badge
						key={index}
						variant="secondary"
						className="bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100 px-2 py-1 text-sm"
					>
						{tag}
						{!disabled && (
							<button
								type="button"
								onClick={(e) => {
									e.stopPropagation();
									removeTag(index);
								}}
								className="ml-1 hover:text-emerald-900 transition-colors"
							>
								<X className="h-3 w-3" />
							</button>
						)}
					</Badge>
				))}
				{!disabled && (
					<Input
						ref={inputRef}
						value={inputValue}
						onChange={(e) => setInputValue(e.target.value)}
						onKeyDown={handleKeyDown}
						placeholder={value.length === 0 ? placeholder : ''}
						className="border-none shadow-none focus-visible:ring-0 focus-visible:ring-offset-0 bg-transparent px-0 py-0 h-6 text-sm flex-1 min-w-[120px]"
						disabled={disabled}
					/>
				)}
			</div>
		</div>
	);
}
